
import { useState, useEffect } from 'react';
import { ParcelDetails } from '@/components/ParcelBookingForm';

export interface ParcelValidationErrors {
  type?: string;
  description?: string;
  weight?: string;
  length?: string;
  width?: string;
  height?: string;
  value?: string;
  otherTypeSpecification?: string;
}

export interface ParcelStepValidationErrors {
  parcels: ParcelValidationErrors[];
  collectionDateTime?: string;
}

export const useParcelValidation = (parcels: ParcelDetails[], collectionDateTime: string) => {
  const [errors, setErrors] = useState<ParcelStepValidationErrors>({ parcels: [] });
  const [isValid, setIsValid] = useState(false);

  const validateParcel = (parcel: ParcelDetails): ParcelValidationErrors => {
    const parcelErrors: ParcelValidationErrors = {};

    // Validate parcel type
    if (!parcel.type || parcel.type.trim() === '') {
      parcelErrors.type = 'Parcel type is required';
    }

    // Validate other type specification when "other" is selected
    if (parcel.type === 'other' && (!parcel.otherTypeSpecification || parcel.otherTypeSpecification.trim() === '')) {
      parcelErrors.otherTypeSpecification = 'Please specify the type of parcel';
    }

    // Validate weight
    if (!parcel.weight || parcel.weight.trim() === '') {
      parcelErrors.weight = 'Weight is required';
    } else if (isNaN(Number(parcel.weight)) || Number(parcel.weight) <= 0) {
      parcelErrors.weight = 'Weight must be a positive number';
    }

    // Validate dimensions
    if (!parcel.dimensions.length || parcel.dimensions.length.trim() === '') {
      parcelErrors.length = 'Length is required';
    } else if (isNaN(Number(parcel.dimensions.length)) || Number(parcel.dimensions.length) <= 0) {
      parcelErrors.length = 'Length must be a positive number';
    }

    if (!parcel.dimensions.width || parcel.dimensions.width.trim() === '') {
      parcelErrors.width = 'Width is required';
    } else if (isNaN(Number(parcel.dimensions.width)) || Number(parcel.dimensions.width) <= 0) {
      parcelErrors.width = 'Width must be a positive number';
    }

    if (!parcel.dimensions.height || parcel.dimensions.height.trim() === '') {
      parcelErrors.height = 'Height is required';
    } else if (isNaN(Number(parcel.dimensions.height)) || Number(parcel.dimensions.height) <= 0) {
      parcelErrors.height = 'Height must be a positive number';
    }

    // Validate insurance value if insurance is enabled
    if (parcel.insuranceEnabled) {
      if (!parcel.value || parcel.value.trim() === '') {
        parcelErrors.value = 'Insurance value is required when insurance is enabled';
      } else if (isNaN(Number(parcel.value)) || Number(parcel.value) <= 0) {
        parcelErrors.value = 'Insurance value must be a positive number';
      }
    }

    return parcelErrors;
  };

  const validateStep = () => {
    const stepErrors: ParcelStepValidationErrors = { parcels: [] };

    // Validate each parcel
    stepErrors.parcels = parcels.map(validateParcel);

    // Validate collection date and time
    if (!collectionDateTime || collectionDateTime.trim() === '') {
      stepErrors.collectionDateTime = 'Collection date and time is required';
    } else {
      const collectionDate = new Date(collectionDateTime);
      const now = new Date();
      if (collectionDate <= now) {
        stepErrors.collectionDateTime = 'Collection time must be in the future';
      }
    }

    setErrors(stepErrors);

    // Check if step is valid (no errors)
    const hasParcelErrors = stepErrors.parcels.some(parcelErrors => 
      Object.keys(parcelErrors).length > 0
    );
    const hasDateTimeError = !!stepErrors.collectionDateTime;
    
    const stepIsValid = !hasParcelErrors && !hasDateTimeError;
    setIsValid(stepIsValid);

    return stepIsValid;
  };

  // Validate whenever data changes
  useEffect(() => {
    validateStep();
  }, [parcels, collectionDateTime]);

  return {
    errors,
    isValid,
    validateStep,
    validateParcel
  };
};
